// languagetools:disable
import type { IDateTime } from '@chaine/types'
import {
  addDays,
  addWeeks,
  format,
  formatDistanceToNow,
  getUnixTime,
  intervalToDuration,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parse,
  parseISO,
  startOfDay,
  startOfToday,
  subDays
} from 'date-fns'
import { enUS } from 'date-fns/locale'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'

/**
 * Format a date in a given format
 * @param date - The date to format
 * @param dateFormat - The format to use
 * @returns The formatted date
 */
export const formatDate = (date: string | Date, dateFormat = 'MM/dd/yyyy hh:mm a'): string => {
  try {
    if (!date) {
      console.warn('No date provided to formatDate')
      return ''
    }

    const parsedDate = typeof date === 'string' ? parseISO(date) : date

    if (!isValid(parsedDate)) {
      console.warn('Invalid date provided to formatDate:', date)
      return ''
    }

    return format(parsedDate, dateFormat)
  } catch (error) {
    console.error('Error formatting date in formatDate:', error)
    return ''
  }
}

/**
 * Method returns whether the given date is before today's date or not
 */
export const isBeforeToday = (date: Date | string): boolean => {
  try {
    if (!date) {
      return false
    }

    const today = startOfToday()
    const parsedDate = typeof date === 'string' ? new Date(date) : date

    // Check if the date is valid
    if (!isValid(parsedDate)) {
      console.warn('Invalid date provided to isBeforeToday:', date)
      return false
    }

    return isBefore(parsedDate, today)
  } catch (error) {
    console.error('Error in isBeforeToday:', error)
    return false
  }
}

/**
 * Formats a date as 24-hour time (HH:mm)
 * @param dateTime - Date object or ISO date string
 * @returns Formatted time string (e.g., "02:23") or empty string if invalid
 */
export const formatDateAsTime = (dateTime: Date | string): string => {
  try {
    // Handle null/undefined
    if (!dateTime) {
      console.warn('No date provided')
      return ''
    }

    // If it's a string, try to parse it
    const parsedDate = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime

    // Check if the date is valid
    if (!isValid(parsedDate)) {
      console.warn('Invalid date provided:', dateTime)
      return ''
    }

    // Use 12-hour US format with AM/PM
    return format(parsedDate, 'h:mm a')
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

/**
 * Returns a compact relative time for recent timestamps:
 * - "just now" if under 1 minute
 * - "X mins ago" if under 60 minutes
 * - "X hours ago" if under 24 hours
 * For older timestamps, caller should fallback to date or time formatting.
 */
export const formatRecentTimeAgo = (dateTime: Date | string): string => {
  try {
    if (!dateTime) return ''
    const date = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime
    if (!isValid(date)) return ''

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    if (diffMs < 0) {
      // Future timestamps: show as "just now"
      return 'just now'
    }

    const diffInMinutes = Math.floor(diffMs / (1000 * 60))
    if (diffInMinutes < 1) return 'just now'
    if (diffInMinutes < 60) {
      const mins = diffInMinutes
      return `${mins} ${mins === 1 ? 'min' : 'mins'} ago`
    }

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      const hours = diffInHours
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`
    }

    // Not recent; let caller decide how to format.
    return ''
  } catch {
    return ''
  }
}
/**
 * Method returns date as Month Date i.e., Oct 24
 */
export const formatDateAsMonthDate = (dateTime: Date | string): string => {
  try {
    // Handle null/undefined
    if (!dateTime) {
      console.warn('No date provided to formatDateAsMonthDate')
      return ''
    }

    // If it's a string, use parseISO to handle ISO date strings correctly (avoids timezone issues)
    // Otherwise use the Date object directly
    const parsedDate = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime

    // Check if the date is valid
    if (!isValid(parsedDate)) {
      console.warn('Invalid date provided to formatDateAsMonthDate:', dateTime)
      return ''
    }

    return format(parsedDate, 'MMM d') // Formats the date as Oct 24
  } catch (error) {
    console.error('Error formatting date in formatDateAsMonthDate:', error)
    return ''
  }
}

/**
 * dateTimeAgo: method is used to get the time ago string from the dateTime
 * @param dateTime timestamp (string | Date)
 * @returns string (hours ago | minutes ago | days ago | years ago)
 */
export const dateTimeAgo = (dateTime: Date | string): string => {
  const date = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime
  if (!isValid(date)) return ''

  return formatDistanceToNow(date, { addSuffix: true })
}

/**
 * dateTimeAgoCompact: compact relative time for table cells.
 * - minutes => min/mins
 * - hours => hr/hrs
 * - days => day/days
 */
export const dateTimeAgoCompact = (dateTime: Date | string): string => {
  const date = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime
  if (!isValid(date)) return ''

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  if (diffMs < 0) return 'just now'

  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes} ${diffMinutes === 1 ? 'min' : 'mins'} ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hr' : 'hrs'} ago`

  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`
}

/**
 * Formats the fetched and last seen time display
 * @param lastLocationFetchedAt - The ISO datetime when the location was last fetched
 * @param lastLocationTimestamp - The timestamp of the last location
 * @returns Formatted string for display
 */
export const formatFetchedAndLastSeenTime = (
  lastLocationFetchedAt: string | null | undefined,
  lastLocationTimestamp: string
): string => {
  const formattedLastSeen = dateTimeAgo(lastLocationTimestamp)

  if (lastLocationFetchedAt && lastLocationFetchedAt !== lastLocationTimestamp) {
    const formattedFetchedAt = dateTimeAgo(lastLocationFetchedAt)
    return `Updated ${formattedFetchedAt} • Last seen ${formattedLastSeen}`
  }
  return `Last seen ${formattedLastSeen}`
}

export const formatFetchedAndLastSeenTimeParts = (
  lastLocationFetchedAt: string | null | undefined,
  lastLocationTimestamp: string
): { updatedText: string | null; lastSeenText: string } => {
  const formattedLastSeen = dateTimeAgo(lastLocationTimestamp)

  if (lastLocationFetchedAt && lastLocationFetchedAt !== lastLocationTimestamp) {
    const formattedFetchedAt = dateTimeAgo(lastLocationFetchedAt)
    return {
      updatedText: `Updated ${formattedFetchedAt}`,
      lastSeenText: `Last seen ${formattedLastSeen}`
    }
  }
  return {
    updatedText: null,
    lastSeenText: `Last seen ${formattedLastSeen}`
  }
}

export const formatDateTime = (date: string | Date, dateFormat?: string): string => {
  try {
    if (!date) {
      console.warn('No date provided to formatDateTime')
      return ''
    }

    const parsedDate = typeof date === 'string' ? parseISO(date) : date

    if (!isValid(parsedDate)) {
      console.warn('Invalid date provided to formatDateTime:', date)
      return ''
    }

    return format(parsedDate, dateFormat ?? 'M/dd/yyyy h:mm a')
  } catch (error) {
    console.error('Error formatting date in formatDateTime:', error)
    return ''
  }
}

/**
 * Method is used to format time in any format we send
 * @param timeString Time string in 24-hour format (HH:mm)
 * @param formatStr Desired output format (default: 'h:mm a')
 * @returns Formatted time string
 */
export const formatTime = (timeString: string, formatStr = 'h:mm a'): string => {
  try {
    // Since we're dealing with just time, we'll use a base date
    const baseDate = new Date()

    // Remove any AM/PM and trim whitespace
    const cleanTimeString = timeString.replace(/\s?[AaPp][Mm]/, '').trim()

    const [hours, minutes] = cleanTimeString.split(':').map(Number)

    // Check if original time was PM and hour is less than 12
    const isPM = /\s?[Pp][Mm]/.test(timeString)
    const adjustedHours = isPM && hours < 12 ? hours + 12 : hours

    // Set the hours and minutes on our base date
    baseDate.setHours(adjustedHours)
    baseDate.setMinutes(minutes)

    return format(baseDate, formatStr)
  } catch (error) {
    console.error('Error formatting time:', error)
    return timeString // Return original string if parsing fails
  }
}

/**
 * Formats a time string with timezone abbreviation
 * @param timeString Time string in 24-hour format (HH:mm)
 * @param timezone IANA timezone string (e.g., 'America/Chicago')
 * @returns Formatted time string with timezone (e.g., "1:33 PM CST")
 */
export const formatTimeWithTimezone = (timeString: string, timezone: string): string => {
  try {
    if (!timeString || !timezone) {
      return timeString || ''
    }

    // Create a date object for today with the given time
    const today = new Date()
    const [hours, minutes] = timeString.split(':').map(Number)

    // Validate time values
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      console.warn('Invalid time values:', timeString)
      return formatTime(timeString)
    }

    // Create an ISO string for today at the given time
    const dateTimeStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`

    // Parse the date string
    const date = parseISO(dateTimeStr)

    // Format time in 12-hour format with AM/PM
    const formattedTime = formatInTimeZone(date, timezone, 'h:mm a')

    // Get timezone abbreviation using Intl.DateTimeFormat
    const timezoneAbbr =
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      })
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName')?.value || ''

    return `${formattedTime} ${timezoneAbbr}`.trim()
  } catch (error) {
    console.error('Error formatting time with timezone:', error)
    // Fallback to regular formatTime if timezone formatting fails
    return formatTime(timeString)
  }
}

export const formatDuration = (duration: number): string => {
  if (duration === 0) return '00:00'

  const durationObj = intervalToDuration({ end: duration * 1000, start: 0 })
  const minutes = durationObj.minutes || 0
  const seconds = durationObj.seconds || 0

  return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
}

/**
 * Converts a time string that may be in 12-hour format (e.g., "6:46 PM" or "6.46 pm")
 * or already in 24-hour format (e.g., "18:46") to a normalized 24-hour
 * time string in the HH:mm format. Returns null when input is empty.
 */
export function to24HourTime(timeInput?: string | null): string | null {
  if (!timeInput) return null

  // Normalize separators and casing
  const normalized = timeInput.trim().replace(/\./g, ':').replace(/\s+/g, ' ').toUpperCase()

  // Match 12/24 hour with optional AM/PM
  const match = normalized.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/)
  if (!match) {
    // Fallback: if it doesn't match expected patterns, return original string
    // so the caller can decide what to do.
    return timeInput
  }

  const [, hourStr, minuteStr, meridiem] = match
  let hour = parseInt(hourStr, 10)
  const minutes = parseInt(minuteStr, 10)

  if (isNaN(hour) || hour < 0 || hour > 23 || isNaN(minutes) || minutes < 0 || minutes > 59) {
    return timeInput
  }

  if (meridiem === 'PM' && hour !== 12) {
    hour += 12
  } else if (meridiem === 'AM' && hour === 12) {
    hour = 0
  }

  return `${hour.toString().padStart(2, '0')}:${minuteStr}`
}

/**
 * Method is used to check if two dates are same or not
 */
export const isDateSame = (
  startDateTime: string | null,
  endDateTime: string | null,
  format = 'dd-MM-yyyy'
): boolean => {
  try {
    // If either date is null, they can't be the same
    if (!startDateTime || !endDateTime) {
      return false
    }

    // Parse both dates using date-fns
    const startDate = parse(startDateTime, format, new Date())
    const endDate = parse(endDateTime, format, new Date())

    // Check if the dates are the same
    return isSameDay(startDate, endDate)
  } catch (error) {
    console.error('Error comparing dates:', error)
    return false // Return false if there's any error in parsing
  }
}

/**
 * Helper function to format time from 24-hour to 12-hour format with AM/PM
 */
export function formatTimeTo12Hour(time24h: string): string {
  if (!time24h) return ''

  try {
    // Validate that the time string matches HH:mm format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    if (!timeRegex.test(time24h)) {
      console.warn('Invalid time format:', time24h)
      return ''
    }

    const [hourStr, minuteStr] = time24h.split(':')
    const hour = parseInt(hourStr, 10)
    const minute = parseInt(minuteStr, 10)

    if (isNaN(hour) || hour < 0 || hour > 23 || isNaN(minute) || minute < 0 || minute > 59) {
      console.warn('Invalid time values:', time24h)
      return ''
    }

    const period = hour >= 12 ? 'PM' : 'AM'
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour

    return `${hour12}:${minuteStr} ${period}`
  } catch (error) {
    console.error('Error formatting time:', error, 'Time value:', time24h)
    return ''
  }
}

/**
 * Will return today, tomorrow, yesterday, otherwise the exact date
 *
 * @param dateTime timestamp (string | Date)
 * @returns object with date, time and past flag
 */
export const getRelativeDateInfo = (
  dateTime: Date | string | null,
  dateFormat = 'M/d/yy',
  // languagetools:disable-next-line
  timeFormat = 'h:mm a XXX'
) => {
  if (!dateTime) return null

  const date = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime
  const today = startOfDay(new Date())
  const tomorrow = startOfDay(addDays(new Date(), 1))
  const yesterday = startOfDay(subDays(new Date(), 1))
  const weekAhead = startOfDay(addWeeks(new Date(), 1))

  // eslint-disable-next-line
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const formattedTime = formatInTimeZone(date, userTimeZone, timeFormat)

  // Check if date is between yesterday and week ahead
  const isAfterYesterday = isAfter(date, yesterday)
  const isBeforeWeekAhead = isBefore(date, weekAhead)

  if (isAfterYesterday && isBeforeWeekAhead) {
    if (isSameDay(date, today)) {
      return { date: 'Today', past: false, time: formattedTime }
    } else if (isSameDay(date, tomorrow)) {
      return { date: 'Tomorrow', past: false, time: formattedTime }
    } else {
      return {
        date: formatInTimeZone(date, userTimeZone, 'EEEE'), // Day of week
        past: false,
        time: formattedTime
      }
    }
  } else {
    if (isSameDay(date, yesterday)) {
      return { date: 'Yesterday', past: true, time: formattedTime }
    }
    return {
      date: formatInTimeZone(date, userTimeZone, dateFormat),
      past: isBefore(date, today),
      time: formattedTime
    }
  }
}

export const formatTimeToISO = (timeInput: Date | string | null): string => {
  if (!timeInput) return ''

  // If it's already a string, try to parse it
  if (typeof timeInput === 'string') {
    try {
      const parsedDate = new Date(timeInput)
      return parsedDate.toISOString()
    } catch (e) {
      return ''
    }
  }

  // Otherwise, it's a Date object
  return timeInput.toISOString()
}

export enum ColorCodeEnum {
  Gray = 'gray',
  Green = 'green',
  Red = 'red',
  Yellow = 'yellow'
}

export type TimeElapsedInfo = {
  color: ColorCodeEnum
  text: string
}

/**
 * Get elapsed time info
 * @param params - Object containing date string and time ago format
 * @returns Object with color and text
 * Example:
 * getElapsedTimeInfo({ dateString: '2024-01-01', timeAgoFormat: 'short' })
 * returns: { color: ColorCodeEnum.Gray, text: '1 day ago' }
 */
export function getElapsedTimeInfo(params: { dateString: string; timeAgoFormat: 'short' | 'long' }): TimeElapsedInfo {
  const { dateString, timeAgoFormat = 'long' } = params

  // Parse date using the same method as the first function
  // The issue might be that you're using different parsing methods
  const date = new Date(dateString)
  const now = new Date()

  // Check if the date is valid
  if (!isValid(date)) {
    console.warn('Invalid date provided to getElapsedTimeInfo:', dateString)
    return { color: ColorCodeEnum.Gray, text: 'Invalid date' }
  }

  // Calculate time differences manually to match moment.js behavior
  const diffMs = now.getTime() - date.getTime()
  const diffInMinutes = Math.floor(diffMs / (1000 * 60))
  const diffInHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  // Time ago string labels
  const timeAgoDayString = timeAgoFormat === 'short' ? 'day' : 'day'
  const timeAgoHourString = timeAgoFormat === 'short' ? 'hr' : 'hour'
  const timeAgoMinuteString = timeAgoFormat === 'short' ? 'min' : 'minute'

  // Determine text
  let text = ''
  if (diffInDays >= 1) {
    text = `${diffInDays} ${timeAgoDayString}${diffInDays > 1 ? 's' : ''}`
  } else if (diffInHours >= 1) {
    text = `${diffInHours} ${timeAgoHourString}${diffInHours > 1 ? 's' : ''}`
  } else if (diffInMinutes === 0) {
    text = 'just now'
  } else {
    text = `${diffInMinutes} ${timeAgoMinuteString}${diffInMinutes > 1 ? 's' : ''}`
  }

  // Determine color
  let color: TimeElapsedInfo['color']
  if (diffInMinutes <= 5) {
    color = ColorCodeEnum.Green
  } else if (diffInMinutes <= 15) {
    color = ColorCodeEnum.Yellow
  } else if (diffInHours < 2) {
    color = ColorCodeEnum.Red
  } else {
    color = ColorCodeEnum.Gray
  }

  return { color, text }
}

// Function to format date and time into a readable format as Day, Month Date, Time
export const formatAppointmentDateTime = (date?: string, time?: string) => {
  if (!date) return 'Appointment Pending'

  try {
    // Parse the date string (assuming format is YYYY-MM-DD)
    const parsedDate = parse(date, 'yyyy-MM-dd', new Date())

    // Check if the parsed date is valid
    if (isNaN(parsedDate.getTime())) {
      return `${date} ${time || ''}`
    }

    // Format the date part
    const formattedDate = format(parsedDate, 'EEE, MMM d')

    // If time is provided, format and append it
    if (time) {
      try {
        // Validate that the time string matches HH:mm format
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
        if (!timeRegex.test(time)) {
          return formattedDate
        }

        // Parse the time (assuming format is HH:MM)
        const [hours, minutes] = time.split(':').map(Number)

        // Validate time values
        if (isNaN(hours) || hours < 0 || hours > 23 || isNaN(minutes) || minutes < 0 || minutes > 59) {
          return formattedDate
        }

        const timeDate = new Date()
        timeDate.setHours(hours)
        timeDate.setMinutes(minutes)

        // Format the time in 12-hour format with AM/PM
        const formattedTime = format(timeDate, 'h:mm a')
        return `${formattedDate}, ${formattedTime}`
      } catch (timeError) {
        console.error('Error formatting time:', timeError, 'Time value:', time)
        return formattedDate
      }
    }

    return formattedDate
  } catch (error) {
    console.error('Error formatting date:', error)
    return `${date} ${time || ''}`
  }
}

/**
 * Validates if the given input is a valid date string.
 */
export function validateDate(dateString: string | null): Date | null {
  if (!dateString) return null

  try {
    const parsedDate = new Date(dateString)

    // Check if the parsed date is valid using date-fns isValid function
    return isValid(parsedDate) ? parsedDate : null
  } catch (error) {
    console.error('Error validating date:', error, 'Date string:', dateString)
    return null
  }
}

/**
 * This function converts a duration in seconds to a string representation of minutes and seconds.
 * @param duration - The duration in seconds.
 * @returns A string representation of minutes and seconds, formatted as "mm:ss minutes" or "ss seconds".
 */
export const getMinutesAndSeconds = (duration: number) => {
  const minutes = Math.floor(duration / 60)
  const seconds = duration % 60

  // languagetools:disable
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds} ${minutes >= 1 ? 'min' : 'sec'}`
}

/**
 * Formats a 24-hour time string to a 12-hour time string.
 * @param time - Time string in 24-hour format (HH:mm)
 * @returns Time string in 12-hour format (hh:mm aa)
 */
export const formatTo12HourTime = (time: string | undefined): string => {
  if (!time) return ''

  try {
    // Validate that the time string matches HH:mm format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
    if (!timeRegex.test(time)) {
      return ''
    }

    const parsedDate = parse(time, 'HH:mm', new Date())

    // Check if the parsed date is valid
    if (isNaN(parsedDate.getTime())) {
      return ''
    }

    return format(parsedDate, 'hh:mm aa')
  } catch (error) {
    console.error('Error formatting time:', error, 'Time value:', time)
    return ''
  }
}

// Helper function to join date and time
export const joinDateTime = (date: string, time?: string) => {
  if (!date || !time) return ''
  return `${date}T${time}`
}

/**
 * Calculate the timestamp for 1 day ago to filter out posts that are too old
 * This ensures we only show posts with pickup dates from yesterday onwards
 * @returns Unix timestamp for 1 day ago at start of day in user's timezone
 */
export const getOneDayAgoTimestamp = (): number => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  const oneDayAgo = toZonedTime(startOfDay(subDays(new Date(), 1)), timezone)
  return getUnixTime(oneDayAgo)
}

/**
 * Determine whether tracking has ended based on a DateTime string
 * @param trackingEndTime - ISO datetime string or other parsable date format
 * @returns true if tracking has ended, false otherwise
 */
export const hasDateTimePassed = (trackingEndTime: string | null | undefined): boolean => {
  if (!trackingEndTime) {
    return false
  }

  try {
    let endDateTime: Date

    // Try multiple parsing strategies
    // 1. Direct Date constructor (works for ISO strings and many other formats)
    endDateTime = new Date(trackingEndTime)

    // If that fails, try parseISO from date-fns
    if (isNaN(endDateTime.getTime())) {
      endDateTime = parseISO(trackingEndTime)
    }

    // Validate the parsed date
    if (isNaN(endDateTime.getTime())) {
      return false
    }

    const now = new Date()

    // Compare timestamps
    const endTimestamp = endDateTime.getTime()
    const nowTimestamp = now.getTime()
    const result = nowTimestamp > endTimestamp
    return result
  } catch (error) {
    return false
  }
}

// Calculate yesterday's date at 00:00:00 to use as minimum date
export const getYesterday = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  yesterday.setHours(0, 0, 0, 0)
  return yesterday
}

// Convert date strings or Date objects to local Date for display
// Handles dates with or without 'Z' suffix
export const parseDateToLocalDateForDisplay = (dateInput: string | Date | null): Date | null => {
  if (!dateInput) return null

  // If it's already a Date object, extract local date components
  if (dateInput instanceof Date) {
    return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate())
  }

  // If string has 'Z', parse as UTC and extract UTC components
  if (dateInput.endsWith('Z')) {
    const date = new Date(dateInput)
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  }

  // If string doesn't have 'Z', parse it directly and extract date components from the string
  // Format: YYYY-MM-DDTHH:mm:ss.sss
  const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})T/)
  if (match) {
    const year = parseInt(match[1], 10)
    const month = parseInt(match[2], 10) - 1 // Month is 0-indexed
    const day = parseInt(match[3], 10)
    return new Date(year, month, day)
  }

  // Fallback: try parsing as-is
  const date = new Date(dateInput)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

/**
 * Formats a date range to a string representation of the date range for filters.
 * @param dateRange - The date range to format.
 * @returns A string representation of the date range, formatted as "yyyy-MM-dd".
 */
export const formatDateRangeForFilters = (date: [Date | null, Date | null]) => {
  const startDate = date[0]
  const endDate = date[1]
  if (!startDate || !endDate) return null
  const year = startDate.getFullYear()
  const month = String(startDate.getMonth() + 1).padStart(2, '0')
  const day = String(startDate.getDate()).padStart(2, '0')

  const endYear = endDate.getFullYear()
  const endMonth = String(endDate.getMonth() + 1).padStart(2, '0')
  const endDay = String(endDate.getDate()).padStart(2, '0')
  return {
    endDate: `${endYear}-${endMonth}-${endDay}T23:59:59.999`,
    startDate: `${year}-${month}-${day}T00:00:00.000`
  }
}

/**
 * This function formats the date and time of the stop
 * in the format of "EEE, MMM dd" and "hh:mm aa"
 */
export const formatDateTimeForStop = (dateTime: IDateTime | null) => {
  if (!dateTime || !dateTime.date) return { date: null, time: null }

  const formattedDate = format(parseISO(dateTime.date), 'EEE, MMM dd')

  // Parse time properly
  if (dateTime.time) {
    // Split time into hours and minutes
    const [hours, minutes] = dateTime.time.split(':')
    const isPM = dateTime.time.toLowerCase().includes('pm')
    const isAM = dateTime.time.toLowerCase().includes('am')
    const parsedHour = parseInt(hours)

    // Convert to 24-hour format
    let hour24: number
    if (isPM) {
      // 12 PM stays as 12, other PM hours add 12 (e.g., 2 PM -> 14)
      hour24 = parsedHour === 12 ? 12 : parsedHour + 12
    } else if (isAM) {
      // 12 AM becomes 0, other AM hours stay as-is (e.g., 9 AM -> 9)
      hour24 = parsedHour === 12 ? 0 : parsedHour
    } else {
      // Already in 24-hour format
      hour24 = parsedHour
    }

    // Format the time
    const timeDate = new Date(2000, 0, 1, hour24, parseInt(minutes))
    const formattedTime = format(timeDate, 'hh:mm aa')

    return { date: formattedDate, time: formattedTime }
  }

  return { date: formattedDate, time: null }
}

/**
 * Formats next-stop ETA in shipment/tracking UIs.
 * Output style mirrors stop columns: `MMM d, HH:mm zzz` when timezone is available.
 * If timezone is not an IANA value (for example `EST`), it appends the provided token.
 */
export const formatNextStopETA = (
  date?: string | null,
  time?: string | null,
  timezone?: string | null
): string | null => {
  if (!date && !time) return null

  const hasTime = typeof time === 'string' && !!time
  const hasDate = typeof date === 'string' && !!date
  const hasTimezone = typeof timezone === 'string' && !!timezone

  if (hasDate && hasTime) {
    const normalizedTime = /^\d{2}:\d{2}$/.test(time as string) ? `${time}:00` : (time as string)
    const dateTimeString = `${date}T${normalizedTime}`

    if (hasTimezone) {
      try {
        Intl.DateTimeFormat(undefined, { timeZone: timezone as string })
        return formatInTimeZone(dateTimeString, timezone as string, 'MMM d, HH:mm zzz', { locale: enUS })
      } catch {
        // non-IANA timezone token (for example EST/CST)
      }
    }

    const parsedDateTime = parseISO(dateTimeString)
    if (isValid(parsedDateTime)) {
      const base = format(parsedDateTime, 'MMM d, HH:mm', { locale: enUS })
      return hasTimezone ? `${base} ${timezone}` : base
    }
  }

  if (hasDate) {
    const parsedDate = parseISO(date as string)
    return isValid(parsedDate) ? format(parsedDate, 'MMM d', { locale: enUS }) : (date as string)
  }

  return (time as string) || null
}
